import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import {
  DeviceType,
  DeviceTypeCreate,
  DeviceTypeUpdate,
  deviceTypesApi,
} from '../services/api'

interface DeviceTypeFormProps {
  initial?: DeviceType | null
  onSubmit: (payload: DeviceTypeCreate | DeviceTypeUpdate) => void
  onCancel: () => void
  loading?: boolean
}

const DeviceTypeForm: React.FC<DeviceTypeFormProps> = ({ initial, onSubmit, onCancel, loading }) => {
  const isEdit = !!initial
  const isBuiltin = initial?.is_builtin ?? false
  const [formData, setFormData] = useState({
    code: initial?.code || '',
    name: initial?.name || '',
    description: initial?.description || '',
    is_active: initial?.is_active ?? true,
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isEdit) {
      const payload: DeviceTypeUpdate = {
        name: formData.name.trim(),
        description: formData.description || undefined,
        is_active: formData.is_active,
      }
      onSubmit(payload)
      return
    }

    const payload: DeviceTypeCreate = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description || undefined,
      is_active: formData.is_active,
    }
    onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
          <input
            className="input"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="rehapiano"
            required
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          className="input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="RehaPiano"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <input
          className="input"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Primary RehaPiano device"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          className="input"
          value={formData.is_active ? 'active' : 'inactive'}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
          disabled={isBuiltin}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {isBuiltin && (
          <p className="text-xs text-gray-500 mt-1">Built-in device types cannot be deactivated.</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}

const DeviceTypesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<DeviceType | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['device-types'],
    queryFn: () => deviceTypesApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (payload: DeviceTypeCreate) => deviceTypesApi.create(payload),
    onSuccess: async () => {
      setIsCreateOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['device-types'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; payload: DeviceTypeUpdate }) =>
      deviceTypesApi.update(params.id, params.payload),
    onSuccess: async () => {
      setIsEditOpen(false)
      setSelectedType(null)
      await queryClient.invalidateQueries({ queryKey: ['device-types'] })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deviceTypesApi.deactivate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['device-types'] })
    },
  })

  const columns: Column<DeviceType>[] = [
    {
      key: 'name',
      header: 'Device Type',
      render: (type) => (
        <div>
          <div className="font-medium text-gray-900">{type.name}</div>
          <div className="text-xs text-gray-500">{type.code}</div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (type) => type.description || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (type) => (
        <span className={`badge ${type.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}>
          {type.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'builtin',
      header: 'Built-in',
      render: (type) => (
        <span className={`badge ${type.is_builtin ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
          {type.is_builtin ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (type) => (
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary text-xs"
            onClick={() => {
              setSelectedType(type)
              setIsEditOpen(true)
            }}
          >
            Edit
          </button>
          {!type.is_builtin && (
            <button
              className="btn-secondary text-xs"
              onClick={() => deactivateMutation.mutate(type.id)}
            >
              Deactivate
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Device Types</h2>
          <p className="text-sm text-gray-600">Manage device type catalog for app compatibility.</p>
        </div>
        <button className="btn-primary" onClick={() => setIsCreateOpen(true)}>
          + Add Device Type
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        getRowKey={(item) => item.id}
        emptyMessage="No device types found"
      />

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create Device Type"
      >
        <DeviceTypeForm
          onSubmit={(payload) => createMutation.mutate(payload as DeviceTypeCreate)}
          onCancel={() => setIsCreateOpen(false)}
          loading={createMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false)
          setSelectedType(null)
        }}
        title="Edit Device Type"
      >
        <DeviceTypeForm
          initial={selectedType}
          onSubmit={(payload) => {
            if (!selectedType) return
            updateMutation.mutate({ id: selectedType.id, payload: payload as DeviceTypeUpdate })
          }}
          onCancel={() => {
            setIsEditOpen(false)
            setSelectedType(null)
          }}
          loading={updateMutation.isPending}
        />
      </Modal>
    </div>
  )
}

export default DeviceTypesPage
